/* P3 — Consent (LGPD) API client + status hook.
 *
 * Backend contract:
 *   POST /api/consents  { type, granted, contentVersion?, patientId?, consultationId?, metadata? }
 *   GET  /api/consents/status                  → map keyed by ConsentType + anonymizationEffective
 *   GET  /api/consents/status/{consentType}    → single-type status
 *
 * Rules:
 *   - Modal-blocking gate is driven by needsAttestation on TERMS_ACCEPTED
 *     and AI_USAGE_PATIENT_NOTICE. Anything else is soft (banner / switch).
 *   - Never send clinical text through this hook — consents are metadata only.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';

export const CONSENT_TYPES = {
  TERMS_ACCEPTED: 'TERMS_ACCEPTED',
  AI_USAGE_PATIENT_NOTICE: 'AI_USAGE_PATIENT_NOTICE',
  PATIENT_DATA_ANONYMIZED: 'PATIENT_DATA_ANONYMIZED',
  COOKIES_ANALYTICS: 'COOKIES_ANALYTICS',
};

export const useConsentStatuses = ({ enabled }) => {
  const [statuses, setStatuses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getConsentStatuses();
      setStatuses(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  const recordConsent = useCallback(async (payload) => {
    const res = await api.postConsent(payload);
    await refresh();
    return res;
  }, [refresh]);

  return { statuses, loading, error, refresh, recordConsent };
};
